import React, { useEffect, useMemo, useState } from 'react';
import { useWorld } from '../../contexts/WorldDataContext';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { NpcShopCard } from '../../components/world/NpcShopCard';
import { useHashScroll } from '../../hooks/useHashScroll';
import { npcs } from '../../data/guide/npcs';
import { npcShopPendings } from '../../data/guide/shops';
import { resolveNpcLocation } from '../../utils/npcLocation';
import { compareMapGroups, locationPoints, serviceLabel, UNLOCATED } from '../../utils/npcShops';
import type { NpcServiceKind } from '../../types/shop';
const FILTERS: (NpcServiceKind | 'todos')[] = ['todos', 'venta', 'reparacion', 'misiones', 'combinaciones', 'acceso'];

/** "NPC y tiendas": NPC por mapa (Lorencia y Noria primero), con ubicación, servicios e inventario. */
export function NpcShopsGuide() {
  const world = useWorld();
  const [service, setService] = useState<NpcServiceKind | 'todos'>('todos');
  useHashScroll();
  useEffect(() => {
    document.title = 'NPC y tiendas · Guía MU Vanguard';
  }, []);
  const groups = useMemo(() => {
    const snap = {
      status: world.status,
      data: world.data,
      error: world.error
    };
    const map = new Map<string, {
      npc: (typeof npcs)[number];
      loc: ReturnType<typeof resolveNpcLocation>;
    }[]>();
    npcs.forEach(npc => {
      if (service !== 'todos' && !npc.services?.some(s => s.kind === service)) return;
      const loc = resolveNpcLocation(npc, snap);
      const names = Array.from(new Set(locationPoints(loc).map(p => p.mapName)));
      // Un NPC que se desplaza entre varios mapas se muestra una sola vez, en el primero.
      const key = names[0] ?? UNLOCATED;
      map.set(key, [...(map.get(key) ?? []), {
        npc,
        loc
      }]);
    });
    return Array.from(map.entries()).sort(([a], [b]) => compareMapGroups(a, b));
  }, [world.status, world.data, world.error, service]);
  return <div className="mx-auto w-full max-w-5xl" data-id="element-2241" data-mp="frc0ngai5nyi">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'NPC y tiendas'
    }]} eyebrow="Ciudades" title="NPC y tiendas" data-id="element-2242" data-mp="ftpu5kpjas3y">
        <p className="mt-3 max-w-2xl text-sm md:text-base text-gray-300" data-id="element-2243" data-mp="37yvrxnzb6uq1">
          Dónde está cada NPC, qué servicio presta y qué vende. Las funciones marcadas como clásicas son de MU Online Season 6 y están pendientes de validar en Vanguard.
        </p>
      </WorldPageHeader>

      <div role="group" aria-label="Filtrar por servicio" className="-mx-4 mb-6 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0" data-id="element-2244" data-mp="2z0aajw5kplqu">
        {FILTERS.map(f => <button key={f} type="button" aria-pressed={service === f} onClick={() => setService(f)} className={`h-9 shrink-0 rounded-full border px-3.5 text-sm font-semibold transition-colors duration-150 ${service === f ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-white' : 'border-white/10 text-gray-300 hover:text-white'}`} data-id="element-2245" data-mp="2p2e4hirhtgzl">
            {f === 'todos' ? 'Todos' : serviceLabel[f]}
          </button>)}
      </div>

      <div className="space-y-10" data-id="element-2246" data-mp="1moa13jptgefu">
        {groups.length === 0 && <p className="text-gray-400" data-id="element-2247" data-mp="1in6l8x7rqiem">Ningún NPC presta este servicio según los datos disponibles.</p>}
        {groups.map(([mapName, list]) => <WorldSection key={mapName} id={`mapa-${mapName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} title={mapName} count={list.length} data-id="element-2248" data-mp="1wk38ypslbjra">
            <div className="grid gap-6 md:grid-cols-2" data-id="element-2249" data-mp="25mchxb87n52k">
              {list.map(({
            npc,
            loc
          }) => <NpcShopCard key={npc.id} npc={npc} loc={loc} w={world.data} data-id="element-2250" data-mp="21l975350rz87" />)}
            </div>
          </WorldSection>)}

        <WorldSection id="datos-que-faltan" title="Datos que faltan" count={npcShopPendings.length} data-id="element-2251" data-mp="2fbmastavorrq">
          <ol className="space-y-3" data-id="element-2252" data-mp="2aem6amtofx2q">
            {npcShopPendings.map((p, i) => <li key={p.what} className="flex gap-3 border-t border-white/10 pt-3 first:border-0 first:pt-0" data-id="element-2253" data-mp="2pa23h0pk093c" data-mpi={i}>
                <span className="font-mono text-sm text-gray-500" data-id="element-2254" data-mp="2kctkixwl0111">{i + 1}</span>
                <span data-id="element-2255" data-mp="hpmws5h3r1cq">
                  <span className="block font-semibold text-white" data-id="element-2256" data-mp="ho1xk2jueu8j">{p.what}</span>
                  <span className="block text-sm text-gray-400" data-id="element-2257" data-mp="mnemqty6sdea">{p.detail}</span>
                </span>
              </li>)}
          </ol>
        </WorldSection>
      </div>
    </div>;
}